import { ChevronRight, Ship, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useRightRail } from "@/context/RightRailContext";
import { ScheduleSailingDetailPanel } from "@/features/schedule/ScheduleSailingDetailPanel";
import { cn } from "@/lib/utils";
import { CollapsibleRailSection } from "@/shell/CollapsibleRailSection";
import {
  railSectionStackClassName,
  railStackInlineScrollBodyClassName,
  railStackSectionContentClassName,
  useRailStackPanel,
} from "@/shell/RailStackContext";
import type { ScheduleResponse } from "@/types/api";

function matchCaption(sailing: ScheduleResponse): string | undefined {
  const parts: string[] = [];
  if (sailing.match_source?.trim()) {
    parts.push(sailing.match_source.trim());
  }
  if (sailing.match_reason?.trim()) {
    parts.push(sailing.match_reason.trim());
  }
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function MatchedScheduleBody({
  sailing,
  onClose,
  showClose,
  stackGrow,
}: {
  sailing: ScheduleResponse;
  onClose?: () => void;
  showClose?: boolean;
  stackGrow?: boolean;
}) {
  const body = (
    <ScheduleSailingDetailPanel
      sailing={sailing}
      matchCaption={matchCaption(sailing)}
    />
  );

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="flex shrink-0 items-start justify-between gap-2">
        <p className="text-xs text-muted-foreground">Matched carrier schedule</p>
        {showClose && onClose ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            aria-label="Close matched schedule"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        ) : null}
      </div>
      {stackGrow ? (
        <div
          className={railStackInlineScrollBodyClassName(
            true,
            "flex flex-col pr-0.5",
          )}
        >
          {body}
        </div>
      ) : (
        body
      )}
    </div>
  );
}

export function BookingMatchedScheduleRailSection({
  sailing,
  stack = false,
  stackAnimate = false,
  stackGrow = false,
}: {
  sailing: ScheduleResponse;
  stack?: boolean;
  stackAnimate?: boolean;
  stackGrow?: boolean;
}) {
  const { toggleBookingMatchedScheduleSection, setBookingMatchedScheduleRail } =
    useRightRail();
  const view = useRailStackPanel("matchedSchedule");

  return (
    <CollapsibleRailSection
      title="Matched schedule"
      icon={Ship}
      expanded={view.expanded}
      accordion={view.accordion}
      fill={view.fill}
      flow={view.flow}
      scroll={stackGrow ? false : view.scroll}
      onToggle={toggleBookingMatchedScheduleSection}
      className={railSectionStackClassName(view, {
        mobileStack: stack,
        stackAnimate,
        stackGrow,
      })}
      contentClassName={stack ? undefined : "pr-0.5"}
    >
      <div className={railStackSectionContentClassName(stackGrow)}>
        <MatchedScheduleBody
          sailing={sailing}
          onClose={() => setBookingMatchedScheduleRail(null)}
          showClose
          stackGrow={stackGrow}
        />
      </div>
    </CollapsibleRailSection>
  );
}

export function BookingMatchedScheduleRail({
  sailing,
  onClose,
}: {
  sailing: ScheduleResponse;
  onClose: () => void;
}) {
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="mx-auto flex max-h-[min(90dvh,var(--app-vvh,90vh))] min-h-0 max-w-lg flex-col rounded-t-2xl pb-safe"
      >
        <SheetHeader className="mb-3 shrink-0 pr-8">
          <SheetTitle>Matched schedule</SheetTitle>
          <SheetDescription>
            Carrier sailing matched to this booking
          </SheetDescription>
        </SheetHeader>
        <div className="sheet-scroll flex min-h-0 flex-1 flex-col">
          <MatchedScheduleBody sailing={sailing} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface MatchedScheduleDetailRowProps {
  vesselLabel?: string;
  matchedSchedule?: ScheduleResponse;
  schedulesLoading?: boolean;
  open: boolean;
  onToggle: () => void;
}

/** Route & schedule — opens the matched sailing in the side rail when available. */
export function MatchedScheduleDetailRow({
  vesselLabel,
  matchedSchedule,
  schedulesLoading = false,
  open,
  onToggle,
}: MatchedScheduleDetailRowProps) {
  const canOpen = Boolean(matchedSchedule);
  const display = vesselLabel?.trim() || "—";

  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="text-sm text-muted-foreground">Vessel</dt>
      <dd className="min-w-0 text-right">
        <button
          type="button"
          disabled={!canOpen}
          aria-expanded={canOpen ? open : undefined}
          aria-haspopup={canOpen ? "dialog" : undefined}
          aria-busy={schedulesLoading || undefined}
          onClick={canOpen ? onToggle : undefined}
          className={cn(
            "inline-flex max-w-full flex-col items-end gap-0.5 rounded-lg px-1.5 py-0.5 text-right transition-colors",
            canOpen && "hover:bg-accent",
            open && canOpen && "bg-accent",
            !canOpen && "cursor-default",
          )}
        >
          <span className="inline-flex items-center gap-1 text-sm font-medium">
            <span className="truncate">{display}</span>
            {canOpen ? (
              <ChevronRight
                className={cn(
                  "size-3.5 shrink-0 text-muted-foreground transition-transform",
                  open && "rotate-90",
                )}
              />
            ) : null}
          </span>
          {canOpen && matchedSchedule?.direct === false ? (
            <span className="text-xs text-muted-foreground">Transshipment</span>
          ) : null}
        </button>
      </dd>
    </div>
  );
}
